import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw, Download, FileText } from "lucide-react";
import SynapseLoader from "@/components/SynapseLoader";

/**
 * Full-screen lightbox for thread attachments.
 * - Images: pinch/wheel zoom + drag pan + double-click zoom
 * - PDFs: native browser viewer via <iframe>
 * - Keyboard: Esc close, ← → nav, +/- zoom, 0 reset
 * - Touch: horizontal swipe between attachments (when not zoomed)
 */
export default function AttachmentLightbox({ open, onClose, threadId, attachments, index, onIndexChange }) {
  const list = attachments || [];
  const current = list[index];
  const total = list.length;

  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const touchRef = useRef(null);

  const isImage = current?.content_type?.startsWith("image/");
  const isPdf = current?.content_type === "application/pdf";
  const previewable = isImage || isPdf;

  // Reset zoom/pan whenever the active attachment changes
  useEffect(() => { setScale(1); setPos({ x: 0, y: 0 }); setFailed(false); }, [index, open]);

  // Load the full-size blob for the active attachment
  useEffect(() => {
    if (!open || !current) return;
    let cancelled = false;
    let url;
    setBlobUrl(null); setLoading(true); setFailed(false);
    (async () => {
      try {
        const resp = await api.get(`/threads/${threadId}/attachments/${current.id}/download`,
                                    { responseType: "blob" });
        url = URL.createObjectURL(resp.data);
        if (!cancelled) { setBlobUrl(url); setLoading(false); }
      } catch {
        if (!cancelled) { setFailed(true); setLoading(false); }
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [open, threadId, current?.id]);

  const goPrev = useCallback(() => {
    if (index > 0) onIndexChange(index - 1);
  }, [index, onIndexChange]);
  const goNext = useCallback(() => {
    if (index < total - 1) onIndexChange(index + 1);
  }, [index, total, onIndexChange]);

  const zoomIn  = useCallback(() => setScale((s) => Math.min(5, +(s + 0.25).toFixed(2))), []);
  const zoomOut = useCallback(() => setScale((s) => Math.max(1, +(s - 0.25).toFixed(2))), []);
  const resetZoom = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }); }, []);

  const downloadCurrent = useCallback(async () => {
    if (!current) return;
    try {
      const resp = await api.get(`/threads/${threadId}/attachments/${current.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = current.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { /* silent */ }
  }, [threadId, current]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); }
      else if (e.key === "ArrowLeft") { goPrev(); }
      else if (e.key === "ArrowRight") { goNext(); }
      else if (e.key === "+" || e.key === "=") { zoomIn(); }
      else if (e.key === "-" || e.key === "_") { zoomOut(); }
      else if (e.key === "0") { resetZoom(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, goPrev, goNext, zoomIn, zoomOut, resetZoom]);

  // Wheel zoom on image only
  const onWheel = (e) => {
    if (!isImage) return;
    e.preventDefault();
    if (e.deltaY < 0) zoomIn(); else zoomOut();
  };

  // Drag pan when zoomed
  const onMouseDown = (e) => {
    if (!isImage || scale <= 1) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, ox: pos.x, oy: pos.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setPos({ x: dragRef.current.ox + (e.clientX - dragRef.current.startX),
             y: dragRef.current.oy + (e.clientY - dragRef.current.startY) });
  };
  const endDrag = () => { dragRef.current = null; };

  // Touch swipe (when not zoomed)
  const onTouchStart = (e) => {
    if (scale > 1) return;
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    if (!touchRef.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;
    touchRef.current = null;
    if (dt < 500 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext(); else goPrev();
    }
  };

  const dblClick = () => {
    if (!isImage) return;
    if (scale === 1) setScale(2); else resetZoom();
  };

  if (!open || !current) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/95 flex flex-col select-none"
      data-testid="attachment-lightbox"
      onMouseMove={onMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-3 md:px-5 py-2.5 border-b border-white/10 bg-black/70 backdrop-blur">
        <div className="min-w-0 flex-1">
          <div className="text-white text-sm font-medium truncate" data-testid="lightbox-filename">{current.filename}</div>
          <div className="text-white/50 text-[11px]">
            {index + 1} / {total} · {current.content_type || "file"}
          </div>
        </div>
        {isImage && (
          <div className="hidden sm:flex items-center gap-1.5 mr-2">
            <button onClick={zoomOut} data-testid="lightbox-zoom-out" title="Riduci (−)"
                    className="p-1.5 rounded-md text-white/80 hover:bg-white/10 disabled:opacity-40"
                    disabled={scale <= 1}>
              <ZoomOut className="h-4 w-4" />
            </button>
            <span className="text-white/70 text-xs w-12 text-center" data-testid="lightbox-zoom-level">
              {Math.round(scale * 100)}%
            </span>
            <button onClick={zoomIn} data-testid="lightbox-zoom-in" title="Ingrandisci (+)"
                    className="p-1.5 rounded-md text-white/80 hover:bg-white/10 disabled:opacity-40"
                    disabled={scale >= 5}>
              <ZoomIn className="h-4 w-4" />
            </button>
            <button onClick={resetZoom} data-testid="lightbox-zoom-reset" title="Reset (0)"
                    className="p-1.5 rounded-md text-white/80 hover:bg-white/10 disabled:opacity-40"
                    disabled={scale === 1 && pos.x === 0 && pos.y === 0}>
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>
        )}
        <button onClick={downloadCurrent} data-testid="lightbox-download"
                className="p-1.5 rounded-md text-white/80 hover:bg-white/10" title="Scarica">
          <Download className="h-4 w-4" />
        </button>
        <button onClick={onClose} data-testid="lightbox-close"
                className="p-1.5 rounded-md text-white/80 hover:bg-white/10" title="Chiudi (Esc)">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <div className="relative flex-1 overflow-hidden flex items-center justify-center" onWheel={onWheel}>
        {/* Nav prev */}
        {index > 0 && (
          <button onClick={goPrev} data-testid="lightbox-prev"
                  className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10
                             p-2 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur">
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        {/* Nav next */}
        {index < total - 1 && (
          <button onClick={goNext} data-testid="lightbox-next"
                  className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10
                             p-2 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur">
            <ChevronRight className="h-6 w-6" />
          </button>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-white/70 flex items-center gap-2" data-testid="lightbox-loading">
            <SynapseLoader size="md" inline /> Caricamento anteprima…
          </div>
        )}

        {/* Failed */}
        {failed && !loading && (
          <div className="text-white/70 text-sm">Anteprima non disponibile. Prova a scaricare il file.</div>
        )}

        {/* Image viewer */}
        {!loading && !failed && isImage && blobUrl && (
          <img
            src={blobUrl}
            alt={current.filename}
            data-testid="lightbox-image"
            draggable={false}
            onMouseDown={onMouseDown}
            onDoubleClick={dblClick}
            style={{
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
              transition: dragRef.current ? "none" : "transform 120ms ease-out",
              cursor: scale > 1 ? (dragRef.current ? "grabbing" : "grab") : "zoom-in",
              maxWidth: "95vw",
              maxHeight: "calc(100vh - 64px)",
              objectFit: "contain",
              userSelect: "none",
            }}
          />
        )}

        {/* PDF viewer */}
        {!loading && !failed && isPdf && blobUrl && (
          <iframe
            src={blobUrl}
            title={current.filename}
            data-testid="lightbox-pdf"
            className="bg-white"
            style={{ width: "95vw", height: "calc(100vh - 64px)", border: 0, borderRadius: 6 }}
          />
        )}

        {/* Non-previewable fallback */}
        {!loading && !failed && !previewable && (
          <div className="flex flex-col items-center gap-3 text-white/80">
            <FileText className="h-16 w-16" />
            <div className="text-sm">Anteprima non supportata per questo tipo di file.</div>
            <button onClick={downloadCurrent}
                    className="px-4 py-2 rounded-md bg-white text-slate-900 text-sm font-medium hover:bg-white/90">
              Scarica {current.filename}
            </button>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="hidden md:block text-center text-white/40 text-[11px] py-2 border-t border-white/10">
        Esc chiudi · ← → naviga · +/− zoom · 0 reset · doppio click per ingrandire
      </div>
    </div>
  );
}
